import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Table, Button, Space, Popconfirm, Input, Select, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import styles from './Vacancies.module.css';
import { columnLabels, STATUSES, FAILURE_REASONS } from '../../constants/vacancyColumnLabels';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import StatusTag from '../StatusTag';
import CompletionModal from '../Candidates/CompletionModal';
import { canEditVacancy } from '../../utils/vacancyPermissions';


const statusOptions = Object.entries(STATUSES).map(([key, value]) => ({
  value: key,
  label: value.title
}));

const ListView = ({
  vacancies,
  onView,
  onEdit,
  onDelete,
  organisations,
  users,
  visibleColumns,
  onStatusChange,
  isCandidateContext = false,
  candidateId = null,
  vacancyByCandidate = [],
  funnelStatuses = [],
  pageSize = 10,
  showSizeChanger = true,
  showQuickJumper = true,
  onReturnToWork,
  canChangeStatus = true,
  canReturnToWork = true,
  userRole = null,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  filters: externalFilters,
  onFiltersChange,
  isModalMode = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isCandidatesPage = location.pathname.includes('/vacancies');
  const [localFilters, setLocalFilters] = useState({});
  const filters = externalFilters !== undefined ? externalFilters : localFilters;
  const setFilters = onFiltersChange || setLocalFilters;
  const [tempFilters, setTempFilters] = useState({});
  const [completionModalVisible, setCompletionModalVisible] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [displayedCount, setDisplayedCount] = useState(10);
  const [modalFilters, setModalFilters] = useState({});

  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  useEffect(() => {
    if (isModalMode) {
      setDisplayedCount(10);
      setModalFilters({});
    }
  }, [vacancies, isModalMode]);

  const getEntityName = (id, entities) => {
    const entity = entities?.find(e => e.organisation_id === id);
    return entity ? entity.name : 'Не указано';
  };

  const getDepartmentName = (departmentId, organisationId, organisations) => {
    const org = organisations.find(o => o.organisation_id === organisationId);
    const department = org?.departments?.find(d => d.id === departmentId);
    return department ? department.name : 'Не указано';
  };

  const getUserName = useCallback((id) => {
    const user = users?.find(u => u.id === id);
    return user ? `${user.first_name} ${user.last_name}` : 'Не указано';
  }, [users]);


  const getCandidateStatusForVacancy = useCallback((vacancyId) => {
    if (!isCandidateContext || !candidateId || !vacancyByCandidate) return null;

    const candidateVacancy = vacancyByCandidate.find(vc =>
      vc.candidate_id === candidateId && vc.vacancy_id === vacancyId
    );

    return candidateVacancy ? candidateVacancy.status_id : null;
  }, [isCandidateContext, candidateId, vacancyByCandidate]);

  const handleTableChange = (pagination, filters, sorter) => {
    const newTempFilters = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key].length > 0) {
        newTempFilters[key] = key === 'status' ? filters[key] : filters[key][0];
      }
    });
    setTempFilters(newTempFilters);
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
  };

  const handleResetFilters = useCallback(() => {
    setTempFilters({});
    setFilters({});
  }, [setFilters]);

  const handleShowMore = useCallback(() => {
    setDisplayedCount(prev => prev + 10);
  }, []);

  // В модальном режиме собираем уникальные компании и отделы из записей
  const availableOrganisations = useMemo(() => {
    if (!isModalMode) return organisations;

    const orgMap = new Map();
    vacancies.forEach(vacancy => {
      if (vacancy.organisation_id) {
        const orgId = String(vacancy.organisation_id);
        if (!orgMap.has(orgId)) {
          const org = organisations?.find(o => String(o.organisation_id) === orgId);
          if (org) {
            orgMap.set(orgId, {
              ...org,
              departments: new Map()
            });
          } else if (vacancy.organisation_name) {
            // Если организации нет в списке, создаем временную запись
            orgMap.set(orgId, {
              organisation_id: vacancy.organisation_id,
              name: vacancy.organisation_name,
              departments: new Map()
            });
          }
        }

        // Добавляем отделы
        if (vacancy.department_id && orgMap.has(orgId)) {
          const deptId = String(vacancy.department_id);
          const org = orgMap.get(orgId);
          if (!org.departments.has(deptId)) {
            // Ищем отдел в исходной организации из списка organisations
            const originalOrg = organisations?.find(o => String(o.organisation_id) === orgId);
            let dept = null;
            if (originalOrg && Array.isArray(originalOrg.departments)) {
              dept = originalOrg.departments.find(d => String(d.id) === deptId);
            }

            if (dept) {
              org.departments.set(deptId, dept);
            } else if (vacancy.department_name) {
              // Если отдела нет в списке, создаем временную запись
              org.departments.set(deptId, {
                id: vacancy.department_id,
                name: vacancy.department_name
              });
            }
          }
        }
      }
    });

    // Преобразуем Map в массив
    return Array.from(orgMap.values()).map(org => ({
      ...org,
      departments: Array.from(org.departments.values())
    }));
  }, [isModalMode, vacancies, organisations]);

  // Применяем фильтры к данным в модальном режиме
  const filteredVacancies = useMemo(() => {
    if (!isModalMode) return vacancies;

    let filtered = vacancies;

    // Применяем фильтры из modalFilters
    Object.keys(modalFilters).forEach(key => {
      const filterValue = modalFilters[key];
      if (filterValue !== null && filterValue !== undefined && filterValue !== '') {
        filtered = filtered.filter(record => {
          if (key === 'status') {
            return String(record[key]) === String(filterValue);
          }
          if (key === 'organisation_id') {
            return String(record[key]) === String(filterValue);
          }
          if (key === 'department_id') {
            return String(record[key]) === String(filterValue);
          }
          const cellValue = record[key];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(String(filterValue).toLowerCase());
        });
      }
    });

    return filtered;
  }, [isModalMode, vacancies, modalFilters]);

  const displayedVacancies = isModalMode
    ? filteredVacancies.slice(0, displayedCount)
    : vacancies;

  const hasMoreVacancies = isModalMode && displayedCount < filteredVacancies.length;

  const checkIfCompletionStatus = useCallback((statusId) => {
    const availableFunnelStatuses = funnelStatuses && funnelStatuses.length > 0 ? funnelStatuses : [];

    const status = availableFunnelStatuses.find(s =>
      (s.id === statusId || s.status_id === statusId)
    );

    if (!status) return false;

    const statusName = status.name || status.status_name || '';

    return statusName.includes('Завершить') ||
      statusName.includes('Трудоустроен') ||
      statusName.includes('Завершено') ||
      statusName.toLowerCase().includes('hired') ||
      statusName.toLowerCase().includes('completed');
  }, [funnelStatuses]);

  const handleStatusChange = useCallback((statusData, newStatusId) => {
    const isCompletionStatus = checkIfCompletionStatus(newStatusId);

    if (isCompletionStatus) {
      setPendingStatusChange({ statusData, newStatusId });
      setCompletionModalVisible(true);
    } else {
      onStatusChange(statusData, newStatusId);
    }
  }, [onStatusChange, checkIfCompletionStatus]);

  const handleCompletionModalConfirm = (completionData) => {
    if (pendingStatusChange) {
      onStatusChange(pendingStatusChange.statusData, pendingStatusChange.newStatusId, completionData);
    }
    setCompletionModalVisible(false);
    setPendingStatusChange(null);
  };

  const handleCompletionModalCancel = () => {
    setCompletionModalVisible(false);
    setPendingStatusChange(null);
  };

  const hasTempFiltersForTitle = Object.keys(tempFilters).length > 0;

  const generateColumns = useMemo(() => {
    if (!visibleColumns) return [];

    const actionColumn = {
      title: isModalMode ? 'Действия' : () => (
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleApplyFilters}
          disabled={!hasTempFiltersForTitle}
          size="small"
        >
          Найти
        </Button>
      ),
      key: 'actions',
      width: isCandidatesPage ? 80 : 120,
      style: {
        wordBreak: 'keep-all',
        whiteSpace: 'nowrap'
      },
      onCell: () => ({
        style: {
          wordBreak: 'keep-all',
          whiteSpace: 'nowrap'
        },
        className: 'actions-column'
      }),
      render: (_, record) => {
        const { canEdit: hasEditPermission } = canEditVacancy(userRole);

        return isCandidatesPage ? (
          <Space size="small">
            <Tooltip title="Просмотреть вакансию">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => navigate(`/vacancies/${record.id}`)}
              />
            </Tooltip>
            <Tooltip title={hasEditPermission ? "Редактировать вакансию" : "У вас нет прав для редактирования вакансии"}>
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => onEdit(record)}
                disabled={!hasEditPermission}
              />
            </Tooltip>
            <Tooltip title={
              !hasEditPermission ? "У вас нет прав для удаления вакансии" :
                record.is_template ? "Удалить вакансию" :
                  "Удаление доступно только для черновиков"
            }>
              <Popconfirm
                title="Удалить вакансию?"
                onConfirm={() => onDelete(record.id)}
                okText="Да"
                cancelText="Нет"
                disabled={!record.is_template || !hasEditPermission}
              >
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  danger
                  disabled={!record.is_template || !hasEditPermission}
                />
              </Popconfirm>
            </Tooltip>
          </Space >
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Link to={`/vacancies/${record.id}`}>
              Перейти
            </Link>
            {record.status_id === 11 && onReturnToWork && canReturnToWork && (
              <Button
                type="link"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => {
                  onReturnToWork(record.id);
                }}
              >
                Вернуть в работу
              </Button>
            )}
          </div>
        );
      }
    };

    const columnRenderMap = {
      job_title: (text, record) => {
        if (isCandidateContext) {
          return `${text || 'Не указано'}`;
        }
        return text || 'Не указано';
      },
      organisation_id: (id, record) => {
        if (record.organisation_name) {
          return record.organisation_name;
        }

        return getEntityName(id, organisations);
      },
      department_id: (departmentId, record) => {
        if (record.department_name) {
          return record.department_name;
        }

        return getDepartmentName(departmentId, record.organisation_id, organisations);
      },
      assigner_id: (id) => getUserName(id),
      status: (status, record) => {
        if (record.is_template) {
          return <StatusTag status="template" statuses={STATUSES} />;
        }
        return <StatusTag status={status} statuses={STATUSES} />;
      },
      failure_reason: (failureReason, record) => {
        if (!isCandidateContext) return failureReason || 'Не указано';

        const candidateVacancy = vacancyByCandidate?.find(vc =>
          vc.candidate_id === candidateId && vc.vacancy_id === record.id
        );

        const reason = candidateVacancy?.failure_reason || failureReason;

        if (!reason) return 'Не указано';


        return FAILURE_REASONS[reason] || reason;
      },
      completion_work: (completionWork, record) => {
        if (!isCandidateContext) return completionWork || 'Не указано';

        const candidateVacancy = vacancyByCandidate?.find(vc =>
          vc.candidate_id === candidateId && vc.vacancy_id === record.id
        );

        const date = candidateVacancy?.completion_work || completionWork;

        if (!date) return 'Не указано';

        return new Date(date).toLocaleDateString('ru-RU');
      },
      result_candidate: (resultCandidate, record) => {
        if (!isCandidateContext) return resultCandidate || 'Не указано';

        const candidateVacancy = vacancyByCandidate?.find(vc =>
          vc.candidate_id === candidateId && vc.vacancy_id === record.id
        );

        const result = candidateVacancy?.result_candidate || resultCandidate;

        if (!result) return 'Не указано';

        if (result === 'success') return 'Успех';
        if (result === 'failure') return 'Провал';
        if (result === 'reserved') return 'В резерве';

        return result;
      },
      candidate_status: (_, record) => {
        if (!isCandidateContext) return null;

        const candidateStatusId = getCandidateStatusForVacancy(record.id);

        const candidateVacancy = vacancyByCandidate?.find(vc =>
          vc.candidate_id === candidateId && vc.vacancy_id === record.id
        );


        const availableFunnelStatuses = candidateStatusId !== 11 ? (Array.isArray(record?.funnel_statuses) && record.funnel_statuses.length > 0
          ? record.funnel_statuses
          : funnelStatuses) : record.funnel_statuses.filter(s => s.status_id === 11);


        if (!candidateStatusId || !availableFunnelStatuses || availableFunnelStatuses.length === 0) {
          return <span>Нет статуса</span>;
        }

        const statusOptions = availableFunnelStatuses
          .map((status) => {
            const rawId = status?.id ?? status?.status_id;
            let label = status?.name ?? status?.status_name ?? 'Без названия';
            if (rawId === undefined || rawId === null) return null;

            const isCurrentStatus = String(rawId) === String(candidateStatusId);
            const isCompletionStatus = label.includes('Завершить') ||
              label.includes('Трудоустроен') ||
              label.toLowerCase().includes('hired');

            if (isCurrentStatus && isCompletionStatus && candidateVacancy?.result_candidate) {
              if (candidateVacancy.result_candidate === 'success') {
                label = 'Успех';
              } else if (candidateVacancy.result_candidate === 'failure') {
                label = 'Провал';
              } else if (candidateVacancy.result_candidate === 'reserved') {
                label = 'В резерве';
              }
            }

            return { value: String(rawId), label };
          })
          .filter(Boolean);

        return (
          <Tooltip title={canChangeStatus ? 'Выберите новый статус кандидата' : 'Нет прав для изменения статуса'}>
            <Select
              style={{ width: '100%', minWidth: 150 }}
              value={statusOptions.some(opt => opt.value === String(candidateStatusId)) ? String(candidateStatusId) : undefined}
              options={statusOptions}
              placeholder={canChangeStatus ? 'Выберите статус' : 'Нет прав для изменения статуса'}
              disabled={!canChangeStatus}
              onChange={(value) => {
                handleStatusChange({
                  id: candidateId,
                  vacancy_id: record.id,
                  status: record.status_id
                }, value);
              }}
            />
          </Tooltip>
        );
      }
    };

    const baseCols = visibleColumns
      .filter(key => key !== 'actions' && key && columnLabels[key])
      .map((key) => ({
        title: columnLabels[key],
        dataIndex: key,
        key,
        render: columnRenderMap[key],
        filteredValue: isModalMode
          ? (modalFilters[key] !== undefined ? (key === 'status' && Array.isArray(modalFilters[key]) ? modalFilters[key] : [modalFilters[key]]) : null)
          : (tempFilters[key] ? (key === 'status' ? (Array.isArray(tempFilters[key]) ? tempFilters[key] : [tempFilters[key]]) : [tempFilters[key]]) : null),
        sorter: key === 'id' ? (a, b) => a.id - b.id : undefined,
        ellipsis: false,
        width: key === 'id' ? 80 : undefined,
        style: {
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          maxWidth: key === 'id' ? 80 : 200
        },
        onCell: () => ({
          style: {
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            maxWidth: key === 'id' ? 80 : 200
          }
        }),
        onFilter: isModalMode ? (value, record) => {
          if (key === 'status') {
            return String(record[key]) === String(value);
          }
          if (key === 'organisation_id') {
            return String(record[key]) === String(value);
          }
          if (key === 'department_id') {
            return String(record[key]) === String(value);
          }
          if (key === 'assigner_id') {
            return String(record[key]) === String(value);
          }
          const cellValue = record[key];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(String(value).toLowerCase());
        } : false,
        filters: isModalMode ? (() => {
          if (key === 'status') {
            // Собираем уникальные статусы из записей
            const statusSet = new Set();
            vacancies.forEach(v => {
              if (v.status) statusSet.add(String(v.status));
            });
            return Array.from(statusSet).map(statusValue => {
              const statusOpt = statusOptions.find(opt => opt.value === statusValue);
              return {
                text: statusOpt ? statusOpt.label : statusValue,
                value: statusValue
              };
            });
          }
          if (key === 'organisation_id') {
            return availableOrganisations?.map(org => ({
              text: org.name,
              value: String(org.organisation_id)
            })) || [];
          }
          if (key === 'department_id') {
            const departmentOptions = [];
            availableOrganisations?.forEach(org => {
              if (org.departments && Array.isArray(org.departments)) {
                org.departments.forEach(dept => {
                  departmentOptions.push({
                    text: `${dept.name} (${org.name})`,
                    value: String(dept.id)
                  });
                });
              }
            });
            return departmentOptions;
          }
          if (key === 'assigner_id') {
            // Собираем уникальных исполнителей из вакансий
            const assignerSet = new Set();
            const assignerMap = new Map();
            vacancies.forEach(v => {
              if (v.assigner_id) {
                const assignerId = String(v.assigner_id);
                if (!assignerSet.has(assignerId)) {
                  assignerSet.add(assignerId);
                  const userName = getUserName(v.assigner_id);
                  assignerMap.set(assignerId, userName);
                }
              }
            });
            return Array.from(assignerMap.entries()).map(([id, name]) => ({
              text: name,
              value: id
            }));
          }
          // Для текстовых полей не используем filters, чтобы работал filterDropdown с onFilter
          return undefined;
        })() : undefined,
        filterDropdown: isModalMode ? (key === 'status' || key === 'organisation_id' || key === 'department_id' || key === 'assigner_id' ? undefined : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
          return (
            <div style={{ padding: 8 }}>
              <Input
                placeholder={`Поиск в ${columnLabels[key]}`}
                value={selectedKeys?.[0] || modalFilters[key] || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedKeys(value ? [value] : []);
                }}
                onPressEnter={() => {
                  const filterValue = selectedKeys?.[0] || '';
                  setModalFilters(prev => ({
                    ...prev,
                    [key]: filterValue || null
                  }));
                  confirm();
                }}
                style={{ width: 188, marginBottom: 8, display: 'block' }}
                allowClear
                onClear={() => {
                  setSelectedKeys([]);
                  setModalFilters(prev => {
                    const newFilters = { ...prev };
                    delete newFilters[key];
                    return newFilters;
                  });
                  clearFilters();
                  confirm();
                }}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={() => {
                    const filterValue = selectedKeys?.[0] || '';
                    setModalFilters(prev => ({
                      ...prev,
                      [key]: filterValue || null
                    }));
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Применить
                </Button>
                <Button
                  onClick={() => {
                    setSelectedKeys([]);
                    setModalFilters(prev => {
                      const newFilters = { ...prev };
                      delete newFilters[key];
                      return newFilters;
                    });
                    clearFilters();
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Сброс
                </Button>
              </Space>
            </div>
          );
        }) : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
          const handleApply = () => {
            const newTempFilters = { ...tempFilters };
            if (selectedKeys && selectedKeys.length > 0) {
              if (key === 'status') {
                newTempFilters[key] = selectedKeys;
              } else {
                newTempFilters[key] = selectedKeys[0];
              }
            } else {
              delete newTempFilters[key];
            }
            setTempFilters(newTempFilters);
            confirm();
          };

          const handleClear = () => {
            const newTempFilters = { ...tempFilters };
            delete newTempFilters[key];
            setTempFilters(newTempFilters);
            clearFilters();
            // Если это был последний фильтр, автоматически сбрасываем все фильтры
            if (Object.keys(newTempFilters).length === 0) {
              handleResetFilters();
            }
            confirm();
          };

          if (key === 'status') {
            return (
              <div style={{ padding: 8 }}>
                <Select
                  placeholder="Выберите статусы"
                  value={selectedKeys}
                  onChange={(value) => setSelectedKeys(value || [])}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                  allowClear
                  mode="multiple"
                  options={statusOptions}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleApply}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Применить
                  </Button>
                  <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                    Сброс
                  </Button>
                </Space>
              </div>
            );
          }

          if (key === 'organisation_id') {
            const orgOptions = organisations?.map(org => ({
              value: String(org.organisation_id),
              label: org.name
            })) || [];

            return (
              <div style={{ padding: 8 }}>
                <Select
                  placeholder="Выберите компанию"
                  value={selectedKeys[0]}
                  onChange={(value) => setSelectedKeys(value ? [value] : [])}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={orgOptions}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleApply}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Применить
                  </Button>
                  <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                    Сброс
                  </Button>
                </Space>
              </div>
            );
          }

          if (key === 'department_id') {
            const departmentOptions = [];
            organisations?.forEach(org => {
              if (org.departments) {
                org.departments.forEach(dept => {
                  departmentOptions.push({
                    value: String(dept.id),
                    label: `${dept.name} (${org.name})`
                  });
                });
              }
            });

            return (
              <div style={{ padding: 8 }}>
                <Select
                  placeholder="Выберите отдел"
                  value={selectedKeys[0]}
                  onChange={(value) => setSelectedKeys(value ? [value] : [])}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={departmentOptions}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleApply}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Применить
                  </Button>
                  <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                    Сброс
                  </Button>
                </Space>
              </div>
            );
          }

          if (key === 'assigner_id') {
            // Фильтруем только рекрутеров (hr, admin)
            const recruiterOptions = users
              ?.filter(user => user.role === 'hr' || user.role === 'admin')
              .map(user => ({
                value: String(user.id),
                label: `${user.first_name} ${user.last_name}`
              })) || [];

            return (
              <div style={{ padding: 8 }}>
                <Select
                  placeholder="Выберите рекрутера"
                  value={selectedKeys[0]}
                  onChange={(value) => setSelectedKeys(value ? [value] : [])}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={recruiterOptions}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleApply}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Применить
                  </Button>
                  <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                    Сброс
                  </Button>
                </Space>
              </div>
            );
          }

          return (
            <div style={{ padding: 8 }}>
              <Input
                placeholder={`Поиск в ${columnLabels[key]}`}
                value={selectedKeys[0]}
                onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                style={{ width: 188, marginBottom: 8, display: 'block' }}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={handleApply}
                  size="small"
                  style={{ width: 90 }}
                >
                  Применить
                </Button>
                <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                  Сброс
                </Button>
              </Space>
            </div>
          );
        },
        filterIcon: (filtered) => {
          if (isModalMode) {
            const hasFilter = modalFilters[key] !== undefined && modalFilters[key] !== null && modalFilters[key] !== '';
            return <SearchOutlined style={{ color: (filtered || hasFilter) ? '#1890ff' : undefined }} />;
          }
          const hasTempFilter = tempFilters[key] !== undefined && tempFilters[key] !== null && tempFilters[key] !== '';
          return <SearchOutlined style={{ color: hasTempFilter ? '#1890ff' : undefined }} />;
        },
      }));

    const includeActions = visibleColumns.includes('actions');
    return includeActions ? [...baseCols, actionColumn] : baseCols;
  }, [visibleColumns, organisations, availableOrganisations, tempFilters, modalFilters, getUserName, isCandidatesPage, onView, onEdit, onDelete, isCandidateContext, candidateId, funnelStatuses, getCandidateStatusForVacancy, handleStatusChange, onReturnToWork, vacancyByCandidate, canChangeStatus, canReturnToWork, navigate, userRole, handleApplyFilters, hasTempFiltersForTitle, handleResetFilters, isModalMode, vacancies, users]);

  const hasActiveFilters = Object.keys(filters).length > 0;

  return (
    <div className={styles.tableContainer}>
      {!isModalMode && hasActiveFilters && (
        <div style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '8px 0'
        }}>
          <Button
            icon={<ClearOutlined />}
            onClick={handleResetFilters}
            size="middle"
          >
            Сбросить фильтры
          </Button>
        </div>
      )}
      {isModalMode && (
        <div style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 0'
        }}>
          <span style={{ color: '#666' }}>
            Показано {displayedVacancies.length} из {filteredVacancies.length} записей {Object.keys(modalFilters).length > 0 && `(всего: ${vacancies.length})`}
          </span>
          {hasMoreVacancies && (
            <Button
              type="primary"
              onClick={handleShowMore}
            >
              Показать еще 10
            </Button>
          )}
        </div>
      )}
      <div style={{
        '--table-cell-word-break': 'break-word',
        '--table-cell-white-space': 'normal'
      }}>
        <Table
          columns={generateColumns}
          dataSource={displayedVacancies}
          rowKey="id"
          bordered
          size="middle"
          locale={{
            triggerDesc: 'Нажмите для сортировки по убыванию',
            triggerAsc: 'Нажмите для сортировки по возрастанию',
            cancelSort: 'Сбросить сортировку',
          }}
          pagination={isModalMode ? false : (currentPage && totalPages !== undefined && onPageChange ? {
            current: currentPage,
            pageSize: pageSize,
            total: totalItems !== undefined && totalItems !== null ? totalItems : (totalPages * pageSize),
            showTotal: (total, range) => {
              const actualTotal = totalItems !== undefined && totalItems !== null ? totalItems : total;
              const endRange = Math.min(range[1], actualTotal);
              return `${range[0]}-${endRange} из ${actualTotal} записей`;
            },
            showSizeChanger: showSizeChanger,
            showQuickJumper: false,
            showLessItems: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, size) => {
              const newSize = size !== undefined ? size : pageSize;
              onPageChange(page, newSize);
            },
            onShowSizeChange: (current, size) => {
              onPageChange(1, size);
            },
          } : {
            pageSize: pageSize,
            showTotal: (total, range) => {
              const actualTotal = vacancies.length;
              const endRange = Math.min(range[1], actualTotal);
              return `${range[0]}-${endRange} из ${actualTotal} записей`;
            },
            showQuickJumper: false,
            defaultPageSize: pageSize
          })}
          scroll={{ x: 'max-content' }}
          onChange={isModalMode ? (pagination, tableFilters, sorter) => {
            // Обновляем modalFilters при изменении фильтров через filters (статус, компания, отдел)
            const newModalFilters = { ...modalFilters };
            Object.keys(tableFilters).forEach(filterKey => {
              if (tableFilters[filterKey] && tableFilters[filterKey].length > 0) {
                if (filterKey === 'status') {
                  newModalFilters[filterKey] = tableFilters[filterKey];
                } else {
                  newModalFilters[filterKey] = tableFilters[filterKey][0];
                }
              } else {
                delete newModalFilters[filterKey];
              }
            });
            setModalFilters(newModalFilters);
          } : handleTableChange}
          onRow={(record) => ({
            onDoubleClick: () => {
              // if (!location.pathname.includes('/vacancies')) {
              //   navigate(`/vacancies?openVacancy=${record.id}`);
              // } else {
              //   onView(record)
              // }
              navigate(`/vacancies/${record.id}`);
            },
            style: { cursor: 'pointer' }
          })}
          className="word-wrap-table"
        />
        <style jsx>{`
          .word-wrap-table .ant-table-cell {
            word-break: break-word !important;
            white-space: normal !important;
            vertical-align: top !important;
          }
          .word-wrap-table .ant-table-cell.actions-column {
            word-break: keep-all !important;
            white-space: nowrap !important;
          }
        `}</style>
      </div>
      <CompletionModal
        visible={completionModalVisible}
        onCancel={handleCompletionModalCancel}
        onConfirm={handleCompletionModalConfirm}
      />
    </div>
  );
};

export default ListView;